/**
 * StaffChatPanel.jsx · v2.7.45+E15 (Phase 5 · Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Staff-facing bilingual live chat console. Three-column layout:
 *
 *   ┌──────────────┬────────────────────────────┬──────────────────┐
 *   │ Session list │ Conversation (bilingual)   │ Tools panel      │
 *   │ • Active     │ Customer row: ES + EN     │ • Archive        │
 *   │ • Archived   │ Staff row:    EN only      │ • Handover →     │
 *   │ • Unread #   │                            │   role / user    │
 *   └──────────────┴────────────────────────────┴──────────────────┘
 *
 * The customer row always shows BOTH the original Spanish + the auto-
 * translated English on the same card so the staff member can pick up
 * nuance the LLM might flatten. The staff input box accepts English
 * only — the translation to the customer's language happens server-side
 * and is never shown to the customer's side.
 *
 * Real-time: subscribes to `/api/ws/dispatch` for chat.* events; falls
 * back to a 10s polling tick if the WS connection ever drops.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MessageCircle, ArrowLeft, Archive, Send, Search, Loader2, Globe,
  Languages, ChevronRight, RefreshCw, ArrowRightLeft, Inbox, CheckCheck,
  Users, Eye, RotateCcw, LifeBuoy, ShieldCheck, X, Link2, PanelLeftClose,
  PanelLeftOpen, MessageSquareText,
} from "lucide-react";

const HANDOVER_ROLES = [
  { value: "dispatcher",         label: "Dispatch", description: "Send to any active dispatcher" },
  { value: "operating_partner",  label: "Operating Partner", description: "Send to a Partner-tier staff" },
  { value: "supervisor",         label: "Supervisor", description: "Send to a field supervisor" },
  { value: "developer",          label: "Developer", description: "Send to an engineer / developer" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function StaffChatPanel() {
  const nav = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [filter, setFilter] = useState("active");  // active | archived
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(urlParams.get("session") || null);
  const [convo, setConvo] = useState({ items: [], session: null });
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHandover, setShowHandover] = useState(false);
  // E108 · Supervisor sign-off state — mandatory resolution log before
  // a ticket linked to this chat can transition status.
  const [showSignoff, setShowSignoff] = useState(false);
  const [signoffPayload, setSignoffPayload] = useState({
    resolution:    "",
    ticket_status: "resolved",
    close_ticket:  true,
    action_type:   "close",
  });
  const [signoffSaving, setSignoffSaving] = useState(false);
  // v2.7.65+E110 · Sidebar collapse toggle (persisted per browser).
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try { return window.localStorage.getItem("bt_chat_sidebar_hidden") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("bt_chat_sidebar_hidden", sidebarHidden ? "1" : "0"); }
    catch { /* quota */ }
  }, [sidebarHidden]);
  // v2.7.65+E110 · Handshake voice-picker modal state
  const [showHandshake, setShowHandshake] = useState(false);
  const [handshake, setHandshake] = useState({
    greeting: "", default: "", preview: "", preview_default: "",
    first_name: "", placeholder_help: "",
  });
  const [handshakeSaving, setHandshakeSaving] = useState(false);
  // v2.7.65+E111 · Curated one-tap templates for the handshake modal.
  const [handshakeTemplates, setHandshakeTemplates] = useState([]);
  // v2.7.65+E115 · Language dropdown metadata + inflight override state.
  const [langOptions, setLangOptions] = useState([]);
  const [langSaving, setLangSaving] = useState(false);
  // v2.7.65+E117 · Agent alias editor
  const [showAlias, setShowAlias] = useState(false);
  const [alias, setAlias] = useState({
    chat_display_name: "", suggested: "", format_hint: "", real_first_name: "", role_label: "",
  });
  const [aliasSaving, setAliasSaving] = useState(false);
  const wsRef = useRef(null);
  const convoEndRef = useRef(null);
  // v2.7.45+E15 · Handover modal — dual-mode (role / specific person)
  const [handoverMode, setHandoverMode] = useState("role"); // 'role' | 'user'
  const [staffOnline, setStaffOnline] = useState([]);

  const reloadSessions = useCallback(async () => {
    try {
      const r = await adminApi.get(`/admin/chat/sessions?status=${filter}`);
      setSessions(r.data?.items || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [filter]);

  const reloadConvo = useCallback(async () => {
    if (!activeId) return;
    try {
      const r = await adminApi.get(`/admin/chat/sessions/${activeId}/messages`);
      setConvo({ items: r.data?.items || [], session: r.data?.session || null });
    } catch (e) { /* silent */ }
  }, [activeId]);

  useEffect(() => { reloadSessions(); }, [reloadSessions]);
  useEffect(() => { reloadConvo(); }, [reloadConvo]);

  // v2.7.65+E109 · Deep-link sync: any change to activeId reflects in the
  // URL so a ticket → chat deep-link is copy-able and back-button friendly.
  useEffect(() => {
    const next = new URLSearchParams(urlParams);
    if (activeId) next.set("session", activeId); else next.delete("session");
    setUrlParams(next, { replace: true });
  }, [activeId, urlParams, setUrlParams]);

  // v2.7.65+E109 · If the deep-link session isn't visible in the current
  // filter (e.g. it's archived but we're on Active), auto-flip the filter
  // so the row shows in the list. Runs once on mount + every time the
  // param changes.
  useEffect(() => {
    const sid = urlParams.get("session");
    if (!sid) return;
    if (sessions.length === 0) return;
    const hit = sessions.find((s) => s.id === sid);
    if (!hit && filter === "active") setFilter("archived");
  }, [urlParams, sessions, filter]);

  // Auto-scroll on new message
  useEffect(() => {
    convoEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [convo.items.length]);

  // WebSocket subscription — instant updates between polls
  useEffect(() => {
    try {
      const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/i, "ws");
      const token = localStorage.getItem("rtg_admin_token");
      if (!wsBase || !token) return;
      const ws = new WebSocket(`${wsBase}/api/ws/dispatch?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m?.type === "chat.message" || m?.type === "chat.session" || m?.type === "chat.archive" || m?.type === "chat.handover") {
            reloadSessions();
            if (m?.session_id === activeId) reloadConvo();
            // E108 · fire the DIRECT chat sound when a message hits MY
            // active session (or the escalation event lands here). This
            // is intentionally louder / different from the generic
            // `rtg:chat_message` broadcast that Katie / bot messages use.
            if (m?.type === "chat.message" && m?.session_id === activeId) {
              try { window.dispatchEvent(new CustomEvent("rtg:chat_message_direct", { detail: { session_id: m.session_id } })); } catch { /* noop */ }
            }
          } else if (m?.type === "chat.escalated") {
            reloadSessions();
            try { window.dispatchEvent(new CustomEvent("rtg:chat_message_direct", { detail: { session_id: m.session_id, reason: m.reason } })); } catch { /* noop */ }
          } else if (m?.type === "chat.signoff") {
            reloadSessions();
            if (m?.session_id === activeId) reloadConvo();
          }
        } catch { /* ignore */ }
      };
      return () => { try { ws.close(); } catch { /* noop */ } };
    } catch { /* noop */ }
    // intentionally depends only on activeId — reloaders are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Polling fallback (every 10s) — covers WS drops
  useEffect(() => {
    const t = setInterval(() => { reloadSessions(); if (activeId) reloadConvo(); }, 10000);
    return () => clearInterval(t);
  }, [reloadSessions, reloadConvo, activeId]);

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return sessions;
    const t = search.trim().toLowerCase();
    return sessions.filter((s) =>
      (s.customer_name || "").toLowerCase().includes(t) ||
      (s.topic || "").toLowerCase().includes(t)
    );
  }, [sessions, search]);

  const send = async () => {
    if (!composer.trim() || !activeId) return;
    setSending(true);
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/messages`, { text: composer });
      setComposer("");
      reloadConvo();
      reloadSessions();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const archive = async () => {
    if (!activeId) return;
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/archive`);
      toast.success("Conversation archived");
      setActiveId(null);
      setConvo({ items: [], session: null });
      reloadSessions();
    } catch (e) { toast.error(`Archive failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const unarchive = async () => {
    if (!activeId) return;
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/unarchive`);
      toast.success("Conversation re-activated");
      reloadConvo();
      reloadSessions();
    } catch (e) { toast.error(`Unarchive failed: ${e?.response?.data?.detail || e.message}`); }
  };

  // v2.7.45+E16 · Phase 6 · promote the current chat to a support ticket.
  // The backend auto-summarises the transcript via the ticket_auto_summarizer
  // prompt, copies the session_id into source_ref, and back-links the
  // chat_session with `linked_ticket_id` / `linked_ticket_no`.
  const promoteToTicket = async () => {
    if (!activeId) return;
    const priority = window.prompt("Priority? (low / normal / high / urgent)", "normal");
    if (!priority) return;
    if (!["low", "normal", "high", "urgent"].includes(priority.trim().toLowerCase())) {
      toast.error("Invalid priority");
      return;
    }
    try {
      const r = await adminApi.post(`/admin/tickets/from-chat/${activeId}`, {
        priority: priority.trim().toLowerCase(),
      });
      toast.success(`Ticket ${r.data?.ticket_no} created`);
      // Re-pull the session so the UI shows the linked ticket
      reloadConvo();
      // Open the new ticket in a fresh tab so the staff doesn't lose the chat
      window.open(`/admin/helpdesk?ticket=${r.data?.id}`, "_blank");
    } catch (e) {
      toast.error(`Promote failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handover = async (role) => {
    if (!activeId) return;
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/handover`, {
        target_kind: "role", target: role, reason: "",
      });
      toast.success(`Routed to ${role.replace("_", " ")}`);
      setShowHandover(false);
      reloadConvo();
    } catch (e) { toast.error(`Handover failed: ${e?.response?.data?.detail || e.message}`); }
  };

  // v2.7.45+E15 · Hand off to one specific person — uses target_kind="user"
  // so the chat is assigned directly to that staff member's id.
  const handoverToUser = async (userId, name) => {
    if (!activeId) return;
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/handover`, {
        target_kind: "user", target: userId, reason: "",
      });
      toast.success(`Assigned to ${name}`);
      setShowHandover(false);
      reloadConvo();
    } catch (e) { toast.error(`Handover failed: ${e?.response?.data?.detail || e.message}`); }
  };

  // Load the live staff list the first time the handover modal opens (cached
  // for subsequent opens; the WS keeps it fresh between manual refreshes).
  useEffect(() => {
    if (!showHandover) return;
    let cancelled = false;
    // E108 · Use the tiered endpoint so we get {tier, tier_label, by_tier}
    // for the escalation dropdown groupings.
    adminApi.get("/admin/chat/staff-online-tiered")
      .then((r) => { if (!cancelled) setStaffOnline(r.data?.items || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [showHandover]);

  // E108 · Submit the mandatory Supervisor sign-off. Server rejects
  // 403 for anyone below L3, so we surface the error inline.
  const submitSignoff = async () => {
    if (!activeId) return;
    const text = (signoffPayload.resolution || "").trim();
    if (text.length < 10) {
      toast.error("Resolution log must be at least 10 characters.");
      return;
    }
    setSignoffSaving(true);
    try {
      await adminApi.post(`/admin/chat/sessions/${activeId}/signoff`, signoffPayload);
      toast.success("Sign-off recorded · ticket transition unlocked.");
      setShowSignoff(false);
      setSignoffPayload({ resolution: "", ticket_status: "resolved",
                          close_ticket: true, action_type: "close" });
      reloadConvo(); reloadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Sign-off failed: ${e.message}`);
    } finally { setSignoffSaving(false); }
  };

  // v2.7.65+E115 · Load language metadata once
  useEffect(() => {
    adminApi.get("/admin/chat/lang-options")
      .then((r) => setLangOptions(r.data?.languages || []))
      .catch(() => {});
  }, []);

  // v2.7.65+E115 · Manual language override — locks the session so
  // auto-detection on subsequent customer messages does not overwrite
  // the admin's choice.
  const overrideLanguage = async (lang) => {
    if (!activeId) return;
    setLangSaving(true);
    try {
      await adminApi.patch(`/admin/chat/sessions/${activeId}/language`,
                            { lang, lock: true });
      toast.success(`Language locked to ${lang.toUpperCase()}`);
      await reloadConvo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Language update failed");
    } finally { setLangSaving(false); }
  };

  // v2.7.65+E117 · Load & save the customer-facing agent alias
  // Load the alias eagerly on mount so the session header can badge
  // the operator's alias without opening the modal first.
  useEffect(() => {
    adminApi.get("/admin/chat/my-alias")
      .then((r) => { if (r.data) setAlias(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!showAlias) return;
    let cancelled = false;
    adminApi.get("/admin/chat/my-alias")
      .then((r) => { if (!cancelled && r.data) setAlias(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showAlias]);
  const saveAlias = async () => {
    const v = (alias.chat_display_name || "").trim();
    if (v.length < 2) { toast.error("Alias must be at least 2 characters"); return; }
    setAliasSaving(true);
    try {
      await adminApi.patch("/admin/chat/my-alias", { chat_display_name: v });
      toast.success("Alias saved — customers will see this name");
      setShowAlias(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setAliasSaving(false); }
  };

  // v2.7.65+E110 · Copy shareable link
  const copyShareLink = async () => {
    if (!activeId) return;
    try {
      const r = await adminApi.get(`/admin/chat/sessions/${activeId}/share-link`);
      const path = r.data?.path || `/admin/chat?session=${activeId}`;
      const absolute = `${window.location.origin}${path}`;
      await navigator.clipboard.writeText(absolute);
      toast.success("Chat link copied to clipboard");
    } catch (e) {
      // Fallback for browsers without clipboard permissions
      try {
        const absolute = `${window.location.origin}/admin/chat?session=${activeId}`;
        await navigator.clipboard.writeText(absolute);
        toast.success("Chat link copied to clipboard");
      } catch {
        toast.error(`Copy failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  // v2.7.65+E110 · Handshake voice-picker · load current greeting on open.
  useEffect(() => {
    if (!showHandshake) return;
    let cancelled = false;
    adminApi.get("/admin/chat/my-handshake")
      .then((r) => { if (!cancelled && r.data) setHandshake(r.data); })
      .catch(() => {});
    // v2.7.65+E111 · Curated templates
    adminApi.get("/admin/chat/handshake-templates")
      .then((r) => { if (!cancelled && r.data) setHandshakeTemplates(r.data.templates || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showHandshake]);

  const saveHandshake = async () => {
    setHandshakeSaving(true);
    try {
      const r = await adminApi.patch("/admin/chat/my-handshake", { greeting: handshake.greeting || "" });
      toast.success("Handshake greeting saved");
      setHandshake((h) => ({ ...h, preview: r.data?.preview || h.preview }));
      setShowHandshake(false);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setHandshakeSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-950" data-testid="staff-chat-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/admin")} className="gap-1.5 -ml-2" data-testid="chat-back">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Button>
          <div className="h-6 w-px bg-slate-300 dark:bg-zinc-700"/>
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0"/>
            <div className="min-w-0">
              <div className="font-bold text-lg truncate">Live Chat · Bilingual</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300">
                <Languages className="inline h-2.5 w-2.5 -mt-0.5 mr-0.5"/>
                English ↔ Spanish · Two-Way Invisible Translation
              </div>
            </div>
          </div>
        </div>
        <Button onClick={reloadSessions} variant="outline" size="sm" disabled={loading} data-testid="chat-refresh" className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
          Refresh
        </Button>
      </div>

      {/* v2.7.65+E110 · Sidebar toggle · pinned to the left edge so the
          operator can hide the sessions list and reclaim the full width
          for the conversation view. Persistent per browser. */}
      <button
        type="button"
        onClick={() => setSidebarHidden((v) => !v)}
        data-testid="chat-sidebar-toggle"
        className={`fixed z-40 top-1/2 -translate-y-1/2 h-16 w-6 rounded-r-md shadow-md text-white flex items-center justify-center transition-all
          ${sidebarHidden
            ? "left-0 bg-emerald-600 hover:bg-emerald-500"
            : "left-72 bg-slate-500 hover:bg-slate-600"}`}
        title={sidebarHidden ? "Show chat sidebar" : "Hide chat sidebar"}
        aria-label={sidebarHidden ? "Show chat sidebar" : "Hide chat sidebar"}
      >
        {sidebarHidden
          ? <PanelLeftOpen className="h-3.5 w-3.5"/>
          : <PanelLeftClose className="h-3.5 w-3.5"/>}
      </button>

      <div className="flex" style={{ minHeight: "calc(100vh - 60px)" }}>
        {/* Column 1: Session list — slides off-screen when sidebarHidden */}
        <aside
          data-testid="chat-sessions-aside"
          className={`flex-shrink-0 border-r border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 overflow-y-auto transition-all duration-300 ease-in-out
            ${sidebarHidden ? "w-0 -translate-x-full opacity-0 pointer-events-none" : "w-72 translate-x-0 opacity-100"}`}
          style={{ maxHeight: "calc(100vh - 60px)" }}
        >
          <div className="p-3 border-b border-slate-200 dark:border-zinc-800 space-y-2">
            <div className="flex gap-1">
              {["active", "archived"].map((k) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={`flex-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded transition-colors
                    ${filter === k
                      ? "bg-emerald-600 text-white"
                      : "bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800"}`}
                  data-testid={`chat-filter-${k}`}>
                  {k}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sessions…"
                className="pl-7 h-8 text-xs" data-testid="chat-search"/>
            </div>
          </div>
          {loading && <div className="p-3 text-xs text-slate-500"><Loader2 className="h-3 w-3 inline animate-spin mr-1"/>Loading…</div>}
          {!loading && filteredSessions.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-500 italic">
              <Inbox className="h-6 w-6 mx-auto mb-2 text-slate-400"/>
              No {filter} sessions.
            </div>
          )}
          {filteredSessions.map((s) => (
            <button key={s.id} type="button"
              onClick={() => setActiveId(s.id)}
              className={`w-full text-left p-3 border-b border-slate-200 dark:border-zinc-800 transition-colors
                ${activeId === s.id
                  ? "bg-emerald-50 dark:bg-emerald-900/20"
                  : "hover:bg-slate-100 dark:hover:bg-zinc-900"}`}
              data-testid={`chat-session-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-sm truncate">{s.customer_name || "Guest"}</div>
                <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded flex-shrink-0
                  ${s.customer_lang === "es"
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                    : "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300"}`}>
                  {s.customer_lang || "en"}
                </span>
              </div>
              {s.topic && <div className="text-[11px] text-slate-500 dark:text-zinc-500 truncate mt-0.5">{s.topic}</div>}
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="text-[10px] text-slate-500 dark:text-zinc-500">{fmtTime(s.last_message_at)}</div>
                {s.unread_count_staff > 0 && (
                  <span className="text-[10px] bg-rose-500 text-white font-bold px-1.5 py-0.5 rounded-full" data-testid={`chat-unread-${s.id}`}>
                    {s.unread_count_staff}
                  </span>
                )}
              </div>
            </button>
          ))}
        </aside>

        {/* Column 2: Conversation */}
        <section className="flex-1 min-w-0 flex flex-col bg-white dark:bg-zinc-950">
          {!activeId && (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageCircle className="h-12 w-12 text-slate-300 dark:text-zinc-700 mx-auto mb-3"/>
                <div className="font-bold text-slate-600 dark:text-zinc-400">Select a conversation</div>
                <div className="text-sm text-slate-500 dark:text-zinc-500 mt-1">Pick a session from the left to start chatting bilingually.</div>
              </div>
            </div>
          )}
          {activeId && (
            <>
              {/* Session header */}
              <div className="border-b border-slate-200 dark:border-zinc-800 p-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate flex items-center gap-2">
                    {convo.session?.customer_name || "Guest"}
                    {/* v2.7.65+E115 · Language dropdown */}
                    <span className="flex items-center gap-1" data-testid="chat-lang-selector">
                      <Languages className="h-3 w-3 text-sky-500"/>
                      <select
                        value={convo.session?.customer_lang || "en"}
                        onChange={(e) => overrideLanguage(e.target.value)}
                        disabled={langSaving}
                        data-testid="chat-lang-dropdown"
                        className="text-[11px] font-bold border border-sky-300 dark:border-sky-800 rounded bg-white dark:bg-zinc-900 px-1.5 py-0.5 text-sky-700 dark:text-sky-300"
                        title="Manually override the customer's active language"
                      >
                        {langOptions.map((l) => (
                          <option key={l.code} value={l.code} data-testid={`chat-lang-opt-${l.code}`}>
                            {l.label} ({l.native})
                          </option>
                        ))}
                      </select>
                      {convo.session?.language_locked ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-600 dark:text-emerald-400"
                              title="Language is locked · auto-detection won't override">
                          Locked
                        </span>
                      ) : convo.session?.customer_lang_auto ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-amber-600 dark:text-amber-400"
                              title="Auto-detected from customer text">
                          Auto
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-zinc-500">
                    Status: <b>{convo.session?.status}</b>
                    {convo.session?.assigned_staff_name && (
                      <>
                        {" · "}Assigned: <b data-testid="chat-header-assigned-alias">
                          {convo.session.assigned_staff_name}
                        </b>
                        {alias?.chat_display_name && (
                          <span className="ml-1 text-[10px] uppercase tracking-widest font-bold text-sky-600 dark:text-sky-300"
                                data-testid="chat-header-own-alias"
                                title="This is the customer-facing alias the rider sees">
                            (you: {alias.chat_display_name})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* v2.7.65+E110 · Copy shareable deep-link */}
                  <Button
                    variant="outline" size="sm" onClick={copyShareLink}
                    className="h-8 gap-1 text-xs"
                    data-testid="chat-copy-link-btn"
                    title="Copy shareable link to this chat session"
                  >
                    <Link2 className="h-3 w-3"/>Copy Link
                  </Button>
                  {/* v2.7.65+E117 · Anonymised customer-facing alias */}
                  <Button
                    variant="outline" size="sm" onClick={() => setShowAlias(true)}
                    className="h-8 gap-1 text-xs border-sky-300 dark:border-sky-800 text-sky-700 dark:text-sky-300"
                    data-testid="chat-edit-alias-btn"
                    title="Set the alias customers see (e.g. Sarah - Dispatcher)"
                  >
                    <ShieldCheck className="h-3 w-3"/>Alias
                  </Button>
                  {/* v2.7.65+E110 · Personal handshake greeting editor */}
                  <Button
                    variant="outline" size="sm" onClick={() => setShowHandshake(true)}
                    className="h-8 gap-1 text-xs border-fuchsia-300 dark:border-fuchsia-800 text-fuchsia-700 dark:text-fuchsia-300"
                    data-testid="chat-edit-handshake-btn"
                    title="Customize your Katie-handoff greeting"
                  >
                    <MessageSquareText className="h-3 w-3"/>Greeting
                  </Button>
                  <Button variant="outline" size="sm" onClick={promoteToTicket} className="h-8 gap-1 text-xs border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300" data-testid="chat-create-ticket-btn">
                    <LifeBuoy className="h-3 w-3"/>Create Ticket
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowHandover(true)} className="h-8 gap-1 text-xs" data-testid="chat-handover-btn">
                    <ArrowRightLeft className="h-3 w-3"/>Handover
                  </Button>
                  {/* E108 · Supervisor sign-off gateway. Rendered for
                      every user but the server enforces L3+ · disabled
                      buttons stay visible so agents know the step exists. */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSignoff(true)}
                    className="h-8 gap-1 text-xs border-blue-400 text-blue-700 dark:text-blue-300"
                    data-testid="chat-signoff-btn"
                    title="Supervisor sign-off · L3+ only · unlocks ticket status transition"
                  >
                    <ShieldCheck className="h-3 w-3"/>Sign-Off
                  </Button>
                  {convo.session?.status === "active" ? (
                    <Button variant="outline" size="sm" onClick={archive} className="h-8 gap-1 text-xs border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300" data-testid="chat-archive-btn">
                      <Archive className="h-3 w-3"/>Archive
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={unarchive} className="h-8 gap-1 text-xs" data-testid="chat-unarchive-btn">
                      <RotateCcw className="h-3 w-3"/>Reactivate
                    </Button>
                  )}
                </div>
              </div>

              {/* Messages — bilingual */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: "calc(100vh - 220px)" }}>
                {convo.items.length === 0 && (
                  <div className="text-center text-sm text-slate-500 italic py-8">No messages yet.</div>
                )}
                {convo.items.map((m) => (
                  <BilingualMessage key={m.id} msg={m}/>
                ))}
                <div ref={convoEndRef}/>
              </div>

              {/* Composer */}
              {convo.session?.status === "active" && (
                <div className="border-t border-slate-200 dark:border-zinc-800 p-3 bg-slate-50/50 dark:bg-zinc-900/30">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 mb-1.5 flex items-center gap-1">
                    <Languages className="h-3 w-3"/>
                    {(() => {
                      const cl = (convo.session?.customer_lang || "en").toLowerCase();
                      if (cl === "en") return "Type in English — the customer reads English too.";
                      const meta = langOptions.find((x) => x.code === cl);
                      return `Type in English — the customer will read ${meta?.label || cl.toUpperCase()} (${meta?.native || cl}) auto-translated.`;
                    })()}
                  </div>
                  <div className="flex items-end gap-2">
                    <Textarea
                      value={composer}
                      onChange={(e) => setComposer(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Type your reply…"
                      rows={2}
                      className="flex-1 text-sm resize-none"
                      data-testid="chat-composer-input"
                    />
                    <Button onClick={send} disabled={sending || !composer.trim()}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 gap-1.5 text-xs flex-shrink-0"
                      data-testid="chat-send-btn">
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Send className="h-3.5 w-3.5"/>}
                      Send
                    </Button>
                  </div>
                </div>
              )}
              {convo.session?.status === "archived" && (
                <div className="border-t border-slate-200 dark:border-zinc-800 p-3 bg-slate-100 dark:bg-zinc-900 text-center text-xs text-slate-600 dark:text-zinc-400 italic">
                  This conversation is archived. Click <b>Reactivate</b> above to reopen the composer.
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Handover modal — dual-mode: role broadcast OR specific person */}
      {showHandover && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowHandover(false)} data-testid="chat-handover-modal">
          <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <ArrowRightLeft className="h-5 w-5 text-emerald-600"/>
              <h3 className="font-bold text-lg">Route this conversation</h3>
            </div>
            {/* Tabs: Role vs Specific person */}
            <div className="flex gap-1 mb-3 bg-slate-100 dark:bg-zinc-900 rounded p-1">
              {[
                { id: "role", label: "By Role" },
                { id: "user", label: "Specific Person" },
              ].map((m) => (
                <button key={m.id} type="button"
                  onClick={() => setHandoverMode(m.id)}
                  className={`flex-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded transition-colors
                    ${handoverMode === m.id
                      ? "bg-white dark:bg-zinc-800 text-emerald-700 dark:text-emerald-300 shadow"
                      : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-zinc-200"}`}
                  data-testid={`chat-handover-tab-${m.id}`}>
                  {m.label}
                </button>
              ))}
            </div>

            {handoverMode === "role" ? (
              <>
                <p className="text-xs text-slate-600 dark:text-zinc-400 mb-3">
                  Hand the chat off to a different team. The next available person in that role will pick it up.
                </p>
                <div className="space-y-2">
                  {HANDOVER_ROLES.map((r) => (
                    <button key={r.value} type="button" onClick={() => handover(r.value)}
                      className="w-full text-left p-3 border border-slate-200 dark:border-zinc-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                      data-testid={`chat-handover-${r.value}`}>
                      <div className="font-bold text-sm flex items-center justify-between">
                        <span><Users className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5"/>{r.label}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400"/>
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">{r.description}</div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-600 dark:text-zinc-400 mb-3">
                  Pick the exact staff member. <span className="inline-flex items-center gap-1 font-bold text-emerald-700 dark:text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>green dot</span> = online now.
                </p>
                <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1" data-testid="chat-handover-user-list">
                  {staffOnline.length === 0 && (
                    <div className="text-center py-6 text-xs text-slate-500 italic">
                      <Loader2 className="h-4 w-4 mx-auto mb-1 animate-spin"/>
                      Loading staff…
                    </div>
                  )}
                  {staffOnline.map((u) => (
                    <button key={u.id} type="button"
                      onClick={() => handoverToUser(u.id, u.name)}
                      className="w-full text-left p-2.5 border border-slate-200 dark:border-zinc-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center gap-2"
                      data-testid={`chat-handover-user-${u.id}`}>
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${u.is_online ? "bg-emerald-500" : "bg-slate-300 dark:bg-zinc-600"}`}/>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-xs truncate">{u.name}</div>
                        <div className="text-[10px] text-slate-500 dark:text-zinc-500 truncate uppercase tracking-widest">
                          {(u.canonical_role || u.role || "").replace(/_/g, " ")}{u.email ? ` · ${u.email}` : ""}
                        </div>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0"/>
                    </button>
                  ))}
                </div>
              </>
            )}
            <Button variant="outline" onClick={() => setShowHandover(false)} className="w-full mt-3" data-testid="chat-handover-cancel">Cancel</Button>
          </div>
        </div>
      )}

      {/* E108 · Supervisor Sign-Off modal ─────────────────────────────
          L3 / L4 / L5 only (server enforces). Ticket status stays
          locked until a resolution log lands here. */}
      {showSignoff && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => !signoffSaving && setShowSignoff(false)}
             data-testid="chat-signoff-modal">
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-2xl w-full max-w-lg p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-500"/>
                Supervisor Sign-Off
              </h3>
              <button type="button" onClick={() => setShowSignoff(false)}
                      disabled={signoffSaving}
                      data-testid="chat-signoff-close"
                      className="text-slate-400 hover:text-slate-700 dark:hover:text-zinc-200">
                <X className="h-4 w-4"/>
              </button>
            </div>
            <div className="text-xs text-slate-500 dark:text-zinc-400 mb-3">
              Required for Level 3+. Write a resolution log then confirm
              to unlock the ticket's status transition.
            </div>

            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 block mb-1">
              Action *
            </label>
            <Select value={signoffPayload.action_type}
                    onValueChange={(v) => setSignoffPayload((p) => ({...p, action_type: v}))}>
              <SelectTrigger data-testid="chat-signoff-action" className="mb-3">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="close">Close & resolve ticket</SelectItem>
                <SelectItem value="transfer_out">Transfer back to lower tier</SelectItem>
                <SelectItem value="refund">Refund approved</SelectItem>
              </SelectContent>
            </Select>

            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 block mb-1">
              Target ticket status
            </label>
            <Select value={signoffPayload.ticket_status}
                    onValueChange={(v) => setSignoffPayload((p) => ({...p, ticket_status: v}))}>
              <SelectTrigger data-testid="chat-signoff-status" className="mb-3">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resolved">resolved</SelectItem>
                <SelectItem value="closed">closed</SelectItem>
                <SelectItem value="refunded">refunded</SelectItem>
              </SelectContent>
            </Select>

            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 block mb-1">
              Resolution log · what happened + how it was resolved *
            </label>
            <Textarea
              rows={4}
              value={signoffPayload.resolution}
              onChange={(e) => setSignoffPayload((p) => ({...p, resolution: e.target.value}))}
              data-testid="chat-signoff-resolution"
              placeholder="e.g. Rider left phone in car #17. Coordinated with driver Mike to hold at HQ overnight; ETA for pickup 8am tomorrow. No refund."
              maxLength={4000}
              className="mb-3"
            />
            <div className="text-[10px] text-slate-400 mb-3">
              {signoffPayload.resolution.length} / 4000 · minimum 10 chars
            </div>

            <label className="flex items-center gap-2 text-xs mb-4 cursor-pointer">
              <input type="checkbox" checked={signoffPayload.close_ticket}
                     onChange={(e) => setSignoffPayload((p) => ({...p, close_ticket: e.target.checked}))}
                     data-testid="chat-signoff-close-ticket"
                     className="h-4 w-4"/>
              Also flip the linked ticket to the target status
            </label>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSignoff(false)}
                      disabled={signoffSaving}
                      data-testid="chat-signoff-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={submitSignoff}
                      disabled={signoffSaving || signoffPayload.resolution.trim().length < 10}
                      data-testid="chat-signoff-submit"
                      className="bg-blue-500 hover:bg-blue-400 text-white gap-1">
                {signoffSaving ? <Loader2 className="h-3 w-3 animate-spin"/> : <ShieldCheck className="h-3 w-3"/>}
                Confirm sign-off
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* v2.7.65+E110 · Handshake voice-picker modal ─────────────────
          Each supervisor can override the Katie-handoff opener with a
          personalized greeting. Supports `{first_name}` token. */}
      {showHandshake && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => !handshakeSaving && setShowHandshake(false)}
             data-testid="chat-handshake-modal">
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-2xl w-full max-w-lg p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <MessageSquareText className="h-5 w-5 text-fuchsia-500"/>
                Handshake voice
              </h3>
              <button type="button" onClick={() => setShowHandshake(false)}
                      disabled={handshakeSaving}
                      data-testid="chat-handshake-close"
                      className="text-slate-400 hover:text-slate-700 dark:hover:text-zinc-200">
                <X className="h-4 w-4"/>
              </button>
            </div>
            <div className="text-xs text-slate-500 dark:text-zinc-400 mb-3">
              Personalize the opener the rider sees on the first Katie handoff.
              Use <code className="font-mono bg-slate-100 dark:bg-zinc-800 px-1 rounded">{"{first_name}"}</code> to
              auto-insert your first name. Leave blank to use the Buller Taxi default.
            </div>

            {/* v2.7.65+E111 · One-tap curated templates */}
            {handshakeTemplates.length > 0 && (
              <div className="mb-3" data-testid="handshake-template-picker">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 mb-1.5">
                  Starter voices
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {handshakeTemplates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setHandshake((h) => ({ ...h, greeting: t.greeting }))}
                      data-testid={`handshake-template-${t.id}`}
                      title={t.tone}
                      className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 hover:border-fuchsia-400 hover:text-fuchsia-600 dark:hover:text-fuchsia-300 transition-colors"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 block mb-1">
              Your greeting
            </label>
            <Textarea
              rows={3}
              value={handshake.greeting || ""}
              onChange={(e) => setHandshake((h) => ({ ...h, greeting: e.target.value }))}
              placeholder={handshake.default}
              maxLength={500}
              data-testid="chat-handshake-input"
              className="mb-1"
            />
            <div className="text-[10px] text-slate-400 mb-3">
              {(handshake.greeting || "").length} / 500
            </div>

            <div className="rounded border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/60 p-3 mb-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 mb-1">
                Preview
              </div>
              <div
                className="text-sm text-slate-800 dark:text-zinc-200 italic"
                data-testid="chat-handshake-preview"
              >
                “{(handshake.greeting || "").trim()
                  ? handshake.greeting.replace(/\{first_name\}/g, handshake.first_name || "Sarah")
                                       .replace(/\{FirstName\}/g, handshake.first_name || "Sarah")
                                       .replace(/\{name\}/g,       handshake.first_name || "Sarah")
                  : (handshake.preview_default || handshake.default)}”
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button
                variant="ghost" size="sm"
                onClick={() => setHandshake((h) => ({ ...h, greeting: "" }))}
                data-testid="chat-handshake-reset"
                className="text-slate-500 hover:text-slate-800 dark:hover:text-zinc-200"
              >
                Reset to default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowHandshake(false)}
                        disabled={handshakeSaving}
                        data-testid="chat-handshake-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={saveHandshake} disabled={handshakeSaving}
                        data-testid="chat-handshake-save"
                        className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white gap-1">
                  {handshakeSaving ? <Loader2 className="h-3 w-3 animate-spin"/> : null}
                  Save greeting
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v2.7.65+E117 · Agent alias modal — first-name-plus-position
          that fully masks the agent's real name / email / id on the
          customer-facing chat stream. */}
      {showAlias && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => !aliasSaving && setShowAlias(false)}
          data-testid="chat-alias-modal"
        >
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-2xl w-full max-w-md p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sky-500"/>
                Customer-facing alias
              </h3>
              <button
                type="button"
                onClick={() => setShowAlias(false)}
                disabled={aliasSaving}
                data-testid="chat-alias-close"
                className="text-slate-400 hover:text-slate-700 dark:hover:text-zinc-200"
              >
                <X className="h-4 w-4"/>
              </button>
            </div>
            <div className="text-xs text-slate-500 dark:text-zinc-400 mb-3">
              Riders will see this name — not your real one. Format:{" "}
              <code className="font-mono bg-slate-100 dark:bg-zinc-800 px-1 rounded">
                First Name - Position
              </code>{" "}
              (e.g. {alias.suggested || "Sarah - Dispatcher"}).
            </div>
            <Input
              value={alias.chat_display_name || ""}
              onChange={(e) => setAlias((a) => ({ ...a, chat_display_name: e.target.value }))}
              placeholder={alias.suggested || "Sarah - Dispatcher"}
              maxLength={60}
              data-testid="chat-alias-input"
              className="mb-2"
              autoFocus
            />
            <div className="text-[10px] text-slate-400 mb-3">
              {(alias.chat_display_name || "").length} / 60
            </div>
            <div className="flex justify-between items-center">
              <Button
                variant="ghost" size="sm"
                onClick={() => setAlias((a) => ({ ...a, chat_display_name: a.suggested }))}
                data-testid="chat-alias-use-suggested"
                className="text-sky-600 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-100"
              >
                Use suggested
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm" onClick={() => setShowAlias(false)}
                  disabled={aliasSaving}
                  data-testid="chat-alias-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm" onClick={saveAlias} disabled={aliasSaving}
                  data-testid="chat-alias-save"
                  className="bg-sky-600 hover:bg-sky-500 text-white gap-1"
                >
                  {aliasSaving ? <Loader2 className="h-3 w-3 animate-spin"/> : null}
                  Save alias
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BilingualMessage({ msg }) {
  const isStaff = msg.direction === "staff";
  // v2.7.65+E115 · Prominent line = the language the CURRENT viewer
  // reads. For the admin console that's always English on customer
  // messages (translated_text) and the customer's language on staff
  // messages (translated_text is the customer's copy of what we sent).
  // We want the ADMIN to see English prominent + original muted, so:
  //   • Customer msg → English (translated) prominent, native muted
  //   • Staff msg    → English (original) prominent, translated hint muted
  const primary = isStaff
    ? { text: msg.original_text,   lang: msg.original_lang   || "en" }
    : { text: msg.translated_text || msg.original_text, lang: msg.translated_lang || "en" };
  const secondary = isStaff
    ? (msg.translated_text && msg.translated_text !== msg.original_text
        ? { text: msg.translated_text, lang: msg.translated_lang || "en" } : null)
    : (msg.original_text && msg.original_text !== primary.text
        ? { text: msg.original_text,   lang: msg.original_lang   || "en" } : null);
  const failed = msg.translation_status === "failed" || msg.translation_retry_needed;
  return (
    <div className={`flex ${isStaff ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.id}`}>
      <div className={`max-w-[80%] ${isStaff ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 flex items-center gap-1">
          <span>{msg.sender_name}</span>
          <span>·</span>
          <span>{fmtTime(msg.created_at)}</span>
          {failed && (
            <span
              data-testid={`chat-msg-translation-failed-${msg.id}`}
              className="ml-1 px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[9px] font-bold uppercase tracking-widest"
              title="Translation fell back to the original text · will retry"
            >
              ⚠ Retry
            </span>
          )}
        </div>
        <div className={`rounded-lg px-3 py-2 ${isStaff ? "bg-emerald-600 text-white" : "bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-zinc-100"}`}>
          {/* Primary (prominent) — the language the ADMIN reads */}
          <div className="text-sm whitespace-pre-wrap break-words">
            <span className={`inline-block text-[9px] uppercase tracking-widest font-bold mr-1.5 px-1 py-0.5 rounded ${isStaff ? "bg-white/20 text-white" : "bg-sky-200 text-sky-900 dark:bg-sky-900/40 dark:text-sky-300"}`}>
              {primary.lang}
            </span>
            {primary.text}
          </div>
          {/* Secondary (muted) — original native OR the outbound translated copy */}
          {secondary && (
            <div className={`text-xs italic mt-1.5 pt-1.5 border-t ${isStaff ? "border-white/30 text-emerald-50/85" : "border-slate-300 dark:border-zinc-700 text-slate-500 dark:text-zinc-400"}`}
                 data-testid={`chat-msg-secondary-${msg.id}`}>
              <span className={`inline-block text-[9px] uppercase tracking-widest font-bold mr-1.5 px-1 py-0.5 rounded ${isStaff ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"}`}>
                {secondary.lang}
              </span>
              {secondary.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
